import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_brand-first-delivery/artifacts/f3s7vusa_Drop24%20White%20Logo%20Cropped.png';

// Pages that have dark hero sections
const darkHeroPages = [];

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Delivery', path: '/delivery' },
  { name: 'Contact', path: '/contact' },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Check if current page has a dark hero
  const isDarkHeroPage = darkHeroPages.includes(location.pathname);
  // Use dark header style when on dark hero page AND not scrolled
  const useDarkStyle = isDarkHeroPage && !isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      data-testid="main-header"
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-soft py-3'
          : useDarkStyle
            ? 'bg-gray-900 py-5'
            : 'bg-white/80 backdrop-blur-sm py-5'
      }`}
      style={{ top: '36px' }}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo - hidden on dark hero pages */}
          {!useDarkStyle && (
            <Link to="/" className="flex-shrink-0" data-testid="header-logo">
              <img
                src={LOGO}
                alt="Drop24 - Ethical Logistics"
                className="h-10 md:h-12 w-auto object-contain"
              />
            </Link>
          )}
          {useDarkStyle && <div className="flex-shrink-0" />}

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                  link.isNew
                    ? useDarkStyle
                      ? 'bg-drop24-red text-white hover:bg-[#c4191f]'
                      : 'bg-drop24-red text-white hover:bg-[#c4191f]'
                    : location.pathname === link.path
                      ? useDarkStyle
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-100 text-drop24-red'
                      : useDarkStyle
                        ? 'text-white/90 hover:bg-white/10'
                        : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {link.name}
                {link.badge && (
                  <span className="text-[10px] font-bold bg-white text-drop24-red px-1.5 py-0.5 rounded uppercase">
                    {link.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="header-cta-button"
              className="inline-flex items-center justify-center bg-drop24-red text-white hover:bg-[#c4191f] px-6 py-2.5 rounded-full font-semibold text-sm transition-all shadow-soft hover:shadow-soft-lg"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-full ${
              useDarkStyle 
                ? 'text-white hover:bg-white/10' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`flex items-center justify-between px-4 py-2 rounded-xl font-medium ${
                    link.isNew
                      ? 'bg-drop24-red text-white'
                      : location.pathname === link.path
                        ? 'bg-drop24-red-light text-drop24-red'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span>{link.name}</span>
                  {link.badge && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${
                      link.isNew ? 'bg-white text-drop24-red' : 'bg-drop24-red text-white'
                    }`}>
                      {link.badge}
                    </span>
                  )}
                </Link>
              ))}
              <div className="pt-4">
                <Link
                  to="/contact"
                  data-testid="mobile-cta-button"
                  className="block w-full text-center bg-drop24-red text-white py-3 rounded-full font-semibold"
                >
                  Get a Quote
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Header;
